import { makeStyles } from "@material-ui/core";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import Loading from "../../../../common-components/Loading";
import {
  getIndirectLaborMonthsAPI,
  getIndirectLaborBudgetMonthsAPI,
  getIndirectNonLaborMonthsAPI,
  getIndirectNonLaborBudgetMonthsAPI,
  getIndirectVersionsAPI,
  downloadIndirectPLAPI,
} from "../../../../api/reports-api";
import { withPeriodFiltersForDownload } from "../filterRelationshipUtils";
import {
  applyNumFilter,
  isNumFilterActive,
  calcOpDropdownPos,
} from "../pl-by-program/plTableFilterUtils";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import { NumericFilterContent, TextFilterContent, OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";
import IndirectPlColumnPicker from "./IndirectPlColumnPicker";
import { PL_POS_C, PL_NEG_C } from "../pl-by-program/plByProgramTheme";

import {
  TABLE_FONT,
  TH_BG,
  CELL_FG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  TOTAL_BG_ALT,
  ACCENT_COLOR,
  TABLE_BORDER,
  CELL_FG_MUTED,
  CELL_FG_STRONG,
  TH_BG_BAND,
  TH_TEXT,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
  Z_HEAD,
  Z_STICKY,
} from "../../reportTableConfig";
const HEADER_BG = TH_BG;
const HEADER_COL = TH_BG;
const ACCENT = ACCENT_COLOR;
const ACCENT2 = "#EEF2FF";
const HAIRLINE = TABLE_BORDER;
const TEXT_MUTED = CELL_FG_MUTED;
const FONT_IP = TABLE_FONT;

const TOTAL_FG = CELL_FG_STRONG;
const varColor = (v) => (v === 0 || v == null ? CELL_FG : v > 0 ? PL_POS_C : PL_NEG_C);

const TOTAL_BG2 = TOTAL_BG_ALT;
const GROUP_BG = "#EEF4FF";

/* ─── Month labels ──────────────────────────────────────────────── */
const IMONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTHS_LOWER = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

/* ─── Quarter / month filter helper ────────────────────────────── */
const QTR_MONTHS = { "1":["Jan","Feb","Mar"], "2":["Apr","May","Jun"], "3":["Jul","Aug","Sep"], "4":["Oct","Nov","Dec"] };
const MONTH_ALIAS = { january:"Jan",february:"Feb",march:"Mar",april:"Apr",may:"May",june:"Jun",july:"Jul",august:"Aug",september:"Sep",october:"Oct",november:"Nov",december:"Dec" };
function deriveActiveMonths(filters) {
  if (filters.monthName?.length) {
    return new Set(filters.monthName.map((m) => MONTH_ALIAS[m.toLowerCase()] ?? m));
  }
  if (filters.quarter?.length) {
    const s = new Set();
    filters.quarter.forEach((q) => (QTR_MONTHS[String(q).replace(/^Q/i, "")] || []).forEach((m) => s.add(m)));
    if (s.size) return s;
  }
  return null;
}

/* ─── Numeric operator definitions ─────────────────────────────── */
const OPS = [
  { key: "gt", label: ">", title: "Greater than" },
  { key: "gte", label: "≥", title: "Greater than or equal" },
  { key: "lt", label: "<", title: "Less than" },
  { key: "lte", label: "≤", title: "Less than or equal" },
  { key: "eq", label: "=", title: "Equal to" },
  { key: "neq", label: "≠", title: "Not equal to" },
];

/* ─── Safe row extractor (handles multiple response shapes) ─────── */
function extractRows(res) {
  if (!res) return [];
  if (Array.isArray(res?.data?.rows)) return res.data.rows;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res)) return res;
  return [];
}

function extractActualMonths(res) {
  const m = res?.data?.actualsMonth ?? res?.actualsMonth;
  return Array.isArray(m) ? m : [];
}

/* ─── API filter builder ────────────────────────────────────────── */
const METRIC_OPTIONS = [
  "Cost Type",
  "Department",
  "Sub Department",
  "Project ID",
  "Account ID & Name",
];

function buildApiFilters(filters = {}, version = null, metricSelection = null) {
  const f = {};
  f.financialYear = filters.year ? String(filters.year) : "All";
  if (version != null) f.version = version;
  if (metricSelection) f.metricSelection = metricSelection;
  if (filters.quarter?.length) f.quarter = filters.quarter;
  if (filters.monthName?.length) f.monthName = filters.monthName;
  if (filters.department?.length) f.department = filters.department;
  if (filters.subDepartment?.length) f.subDepartment = filters.subDepartment;
  if (filters.employeeName?.length) f.employeeName = filters.employeeName;
  if (filters.costTypeIndirect?.length)
    f.costTypeIndirect = filters.costTypeIndirect;
  return f;
}

/* ─── Version normalizer ────────────────────────────────────────── */
function normalizeVersions(data = []) {
  return data
    .map((v) => {
      if (typeof v === "object" && v !== null) {
        const id = v.key ?? v.id ?? v.version ?? v.versionId ?? v.version_id;
        const label = v.name ?? v.version_name ?? v.versionName ?? String(id);
        return { id, label };
      }
      return { id: v, label: String(v) };
    })
    .filter((v) => v.id != null);
}

/* ─── Data transformation helpers ──────────────────────────────── */
function vAdd(a, b) {
  return a.map((v, i) => v + (b[i] || 0));
}
function vSub(a, b) {
  return a.map((v, i) => v - (b[i] || 0));
}
function vSum(v) {
  return v.reduce((s, x) => s + x, 0);
}
function zeroV() {
  return Array(12).fill(0);
}

function laborRowV(row) {
  return MONTHS_LOWER.map((m) => row[m] || 0);
}

function nonLaborRowV(row) {
  return MONTHS_LOWER.map((m) => row[m] || 0);
}

function buildLaborGroup(rows) {
  const v = rows.reduce((acc, row) => vAdd(acc, laborRowV(row)), zeroV());
  const total = vSum(v);
  return {
    id: "il",
    type: "Indirect Labor",
    isGroup: true,
    v,
    total,
    children: [{ type: "Labor", v: [...v], total }],
  };
}

function buildNonLaborGroup(rows) {
  const children = rows.map((row) => {
    const type = [row.accountId, row.accountName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const v = nonLaborRowV(row);
    return { type, v, total: vSum(v) };
  });
  const v = children.reduce((acc, c) => vAdd(acc, c.v), zeroV());
  return {
    id: "inl",
    type: "Indirect Non Labor",
    isGroup: true,
    v,
    total: vSum(v),
    children,
  };
}

function buildGroups(laborRows, nonLaborRows) {
  return [buildLaborGroup(laborRows), buildNonLaborGroup(nonLaborRows)];
}

function buildVarianceGroups(afGroups, bdgGroups) {
  const bdgByType = new Map(bdgGroups.map((g) => [g.type, g]));
  return afGroups.map((afGrp) => {
    const bdgGrp = bdgByType.get(afGrp.type);
    const v = vSub(afGrp.v, bdgGrp?.v || zeroV());

    const bdgChildMap = new Map(
      (bdgGrp?.children || []).map((c) => [c.type, c]),
    );
    const afChildMap = new Map(afGrp.children.map((c) => [c.type, c]));
    const allTypes = [
      ...new Set([...afChildMap.keys(), ...bdgChildMap.keys()]),
    ];
    const children = allTypes.map((type) => {
      const cv = vSub(
        afChildMap.get(type)?.v || zeroV(),
        bdgChildMap.get(type)?.v || zeroV(),
      );
      return { type, v: cv, total: vSum(cv) };
    });

    return {
      id: afGrp.id,
      type: afGrp.type,
      isGroup: true,
      v,
      total: vSum(v),
      children,
    };
  });
}

function computeGrandTotalV(groups) {
  return groups.reduce((acc, g) => vAdd(acc, g.v), zeroV());
}

/* ─── Formatter ─────────────────────────────────────────────────── */
const fmtI = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `($${abs})` : `$${abs}`;
};

/* ─── Inline table cell styles ──────────────────────────────────── */
const GH = 36,
  CH = 38,
  RH = 44;
const TOTAL_TOP = GH + CH - 1;
const TYPE_W = 260,
  COL_W = 96,
  TOTAL_W = 130;
const TH_SH_G = "inset 0 -1px 0 #4a4a4c";
const TH_SH = "inset 0 -1px 0 #4a4a4c";

const thCore = (top, h, z, pad, bg) => ({
  position: "sticky",
  top,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: bg,
  color: TH_TEXT,
  verticalAlign: "middle",
  padding: pad,
  whiteSpace: "nowrap",
  borderRight: "1px solid #4a4a4c",
  borderBottom: "none",
});
const thFixedBlank = () => ({
  ...thCore(0, GH, Z_HEAD + 1, 0, HEADER_BG),
  left: 0,
  width: TYPE_W,
  minWidth: TYPE_W,
  maxWidth: TYPE_W,
  boxShadow: TH_SH_G,
});
const thBandGroup = () => ({
  ...thCore(0, GH, Z_HEAD, "7px 14px", HEADER_BG),
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  textAlign: "center",
  boxShadow: TH_SH_G,
  borderRight: "none",
});
const thFixedCol = () => ({
  ...thCore(GH, CH, Z_HEAD + 1, "0 14px", HEADER_COL),
  left: 0,
  width: TYPE_W,
  minWidth: TYPE_W,
  maxWidth: TYPE_W,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "left",
  boxShadow: `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH}`,
});
const thMonth = (isActual = false) => ({
  ...thCore(GH, CH, Z_HEAD, "0 10px", HEADER_COL),
  fontSize: 11,
  fontWeight: isActual ? 700 : 600,
  textAlign: "right",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  boxShadow: TH_SH,
  ...(isActual ? { background: TH_ACTUAL_BG, color: TH_ACTUAL_TEXT } : {}),
});
const thTotal = () => ({ ...thMonth(false), fontWeight: 700 });

const tdBase = (bg, align = "right", fw = 400) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: align,
  fontWeight: fw,
  padding: "7px 8px",
  fontSize: 12,
  color: CELL_FG,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});
const tdFixed = (bg, fw = 400) => ({
  ...tdBase(bg, "left", fw),
  position: "sticky",
  left: 0,
  zIndex: Z_STICKY,
  width: TYPE_W,
  minWidth: TYPE_W,
  maxWidth: TYPE_W,
  padding: "7px 14px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
});
const tdTotalFixed = () => ({
  ...tdFixed(TOTAL_BG, 700),
  color: TOTAL_FG,
  borderBottom: "1px solid #CBD5E1",
  position: "sticky",
  top: TOTAL_TOP,
  zIndex: Z_STICKY + 2,
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06), 0 1px 0 #CBD5E1",
});
const tdTotalScroll = () => ({
  ...tdBase(TOTAL_BG, "right", 600),
  color: TOTAL_FG,
  borderBottom: "1px solid #CBD5E1",
  position: "sticky",
  top: TOTAL_TOP,
  zIndex: 2,
  boxShadow: "0 1px 0 #CBD5E1",
});
const tdTotalCol = (grand) => ({
  ...(grand ? tdTotalScroll() : tdBase("#F8FAFC", "right", 700)),
  background: grand ? TOTAL_BG2 : "#F8FAFC",
  borderBottom: grand ? "1px solid #CBD5E1" : "1px solid #E2E8F0",
});
const tdGroupFixed = () => ({
  ...tdFixed(GROUP_BG, 600),
  color: "#1E3A5F",
  borderBottom: "1px solid #DBEAFE",
});
const tdGroupScroll = () => ({
  ...tdBase(GROUP_BG, "right", 500),
  color: "#1E3A5F",
  borderBottom: "1px solid #DBEAFE",
});

/* ─── makeStyles ────────────────────────────────────────────────── */
const useStyles = makeStyles(() => ({
  tableSection: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: 20,
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "18px 28px",
    borderBottom: "2px solid #F0F2F5",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexShrink: 0,
    background: "#fff",
    position: "relative",
    zIndex: 20,
    overflow: "visible",
  },
  tableWrap: {
    overflow: "auto",
    maxHeight: "calc(100vh - 320px)",
    background: "#fff",
  },
  tableBody: {
    overflow: "hidden",
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: "#111827",
    margin: 0,
    fontFamily: FONT_IP,
    letterSpacing: "-0.02em",
  },
  sectionMeta: {
    fontSize: 12,
    color: TEXT_MUTED,
    marginTop: 4,
    fontFamily: FONT_IP,
  },
  headRight: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  colPickerToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 12,
    position: "relative",
    zIndex: 30,
  },

  colPickerWrap: { position: "relative" },
  colsBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E2E8F0",
    background: "#fff",
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    fontFamily: FONT_IP,
    whiteSpace: "nowrap",
    "&:hover": { background: "#F9FAFB", borderColor: "#C7D2FE" },
  },
  colsBtnActive: { borderColor: "#C7D2FE", background: ACCENT2, color: ACCENT },
  hiddenBadge: {
    display: "inline-flex",
    alignItems: "center",
    height: 18,
    padding: "0 7px",
    borderRadius: 10,
    background: ACCENT,
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
  },
  colPicker: {
    position: "absolute",
    top: "calc(100% + 4px)",
    right: 0,
    zIndex: 1200,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 10,
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
    minWidth: 180,
    maxHeight: 420,
    overflowY: "auto",
    fontFamily: FONT_IP,
  },
  colPickerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px 8px",
    borderBottom: "1px solid #F3F4F6",
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 1,
  },
  colPickerTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: TEXT_MUTED,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  colPickerReset: {
    fontSize: 11,
    fontWeight: 600,
    color: ACCENT,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    fontFamily: FONT_IP,
    "&:hover": { textDecoration: "underline" },
  },
  colPickerSection: {
    fontSize: 10,
    fontWeight: 700,
    color: TEXT_MUTED,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "8px 14px 4px",
  },
  colPickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 12,
    color: "#374151",
    userSelect: "none",
    borderBottom: "1px solid #F9FAFB",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { background: "#F9FAFB" },
  },
  colPickerCheck: {
    width: 14,
    height: 14,
    accentColor: ACCENT,
    cursor: "pointer",
    flexShrink: 0,
  },

  filterIconBtn: {
    flexShrink: 0,
    width: 16,
    height: 16,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    borderRadius: 3,
    color: "#fff",
    opacity: 0.45,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { opacity: 1, background: "rgba(255,255,255,0.18)" },
  },
  filterIconBtnActive: {
    opacity: 1,
    color: "#A5B4FC",
    background: "rgba(165,180,252,0.22)",
    borderRadius: 3,
  },

  filterPanel: {
    position: "fixed",
    zIndex: 99,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "10px 12px",
    minWidth: 180,
    fontFamily: FONT_IP,
  },
  filterTextInput: {
    flex: 1,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 7px",
    outline: "none",
    fontFamily: FONT_IP,
    background: "#fff",
    color: "#111827",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#9CA3AF" },
  },
  filterNumRow: { display: "flex", alignItems: "center", gap: 3, height: 26 },
  filterNumInput: {
    width: "100%",
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    fontSize: 11,
    padding: "0 5px",
    outline: "none",
    fontFamily: FONT_IP,
    background: "#fff",
    color: "#111827",
    textAlign: "right",
    "&:focus": { borderColor: ACCENT },
    "&::placeholder": { color: "#CBD5E1" },
  },
  filterClearBtn: {
    flexShrink: 0,
    width: 18,
    height: 18,
    border: "none",
    borderRadius: "50%",
    background: "#E2E8F0",
    fontSize: 9,
    color: "#64748B",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { background: "#CBD5E1" },
  },
  opBtn: {
    flexShrink: 0,
    width: 22,
    height: 26,
    border: "1.5px solid #CBD5E1",
    borderRadius: 5,
    background: "#fff",
    fontSize: 11,
    fontWeight: 700,
    color: ACCENT,
    cursor: "pointer",
    fontFamily: FONT_IP,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    "&:hover": { background: ACCENT2, borderColor: ACCENT },
  },
  opBtnActive: {
    background: ACCENT,
    borderColor: ACCENT,
    color: "#fff",
    "&:hover": { background: "#4338CA" },
  },
  opDropdown: {
    position: "fixed",
    zIndex: 99,
    background: "#fff",
    border: "1px solid #E2E8F0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    minWidth: 190,
    padding: "4px 0",
    fontFamily: FONT_IP,
  },
  opOption: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "8px 14px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    color: "#374151",
    fontFamily: FONT_IP,
    textAlign: "left",
    "&:hover": { background: "#F9FAFB" },
  },
  opOptionActive: {
    background: ACCENT2,
    color: ACCENT,
    fontWeight: 600,
    "&:hover": { background: ACCENT2 },
  },
  opSymbol: {
    fontWeight: 700,
    fontSize: 15,
    color: ACCENT,
    width: 18,
    textAlign: "center",
    flexShrink: 0,
  },
  opTitle: { fontSize: 12 },
  clearFiltersBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 30,
    padding: "0 10px",
    borderRadius: 6,
    border: "1px solid #FCA5A5",
    background: "#FFF1F2",
    fontSize: 12,
    fontWeight: 500,
    color: "#DC2626",
    cursor: "pointer",
    fontFamily: FONT_IP,
    whiteSpace: "nowrap",
    "&:hover": { background: "#FEE2E2" },
  },

  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 20px",
    borderTop: `1px solid ${HAIRLINE}`,
    background: "#FAFAFA",
    flexShrink: 0,
    gap: 12,
    flexWrap: "wrap",
  },
  paginationInfo: {
    fontSize: 12,
    color: TEXT_MUTED,
    fontFamily: FONT_IP,
    whiteSpace: "nowrap",
  },
  paginationRight: { display: "flex", alignItems: "center", gap: 8 },
  pageSizeLabel: {
    fontSize: 12,
    color: TEXT_MUTED,
    fontFamily: FONT_IP,
    whiteSpace: "nowrap",
  },
  pageSizeSelect: {
    height: 28,
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    fontSize: 12,
    color: "#374151",
    padding: "0 6px",
    background: "#fff",
    cursor: "pointer",
    outline: "none",
    fontFamily: FONT_IP,
    "&:focus": { borderColor: ACCENT },
  },
  paginationControls: { display: "flex", alignItems: "center", gap: 3 },
  pageBtn: {
    minWidth: 30,
    height: 28,
    padding: "0 6px",
    border: "1px solid #E2E8F0",
    borderRadius: 6,
    background: "#fff",
    fontSize: 12,
    color: TH_BG_BAND,
    cursor: "pointer",
    fontFamily: FONT_IP,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    "&:hover:not(:disabled)": { background: "#F1F5F9", borderColor: "#94A3B8" },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  },
  pageBtnActive: {
    background: TH_BG_BAND,
    borderColor: TH_BG_BAND,
    color: "#fff",
    fontWeight: 600,
    "&:hover": { background: TH_BG_BAND },
  },
  pageDots: {
    fontSize: 12,
    color: TEXT_MUTED,
    padding: "0 4px",
    userSelect: "none",
  },

  /* loading / error */
  stateBox: {
    padding: "40px 24px",
    textAlign: "center",
    fontFamily: FONT_IP,
    fontSize: 13,
  },
  loadingText: { color: TEXT_MUTED },
  errorText: { color: "#DC2626" },
  retryBtn: {
    marginTop: 10,
    padding: "5px 16px",
    borderRadius: 6,
    border: "1px solid #FCA5A5",
    background: "#FFF1F2",
    color: "#DC2626",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: FONT_IP,
  },
}));

/* ─── VersionPicker ─────────────────────────────────────────────── */
function VersionPicker({ versions, value, onChange, loading }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        border: "1.5px solid #E0E7FF",
        borderRadius: 8,
        overflow: "hidden",
        height: 34,
        fontFamily: FONT_IP,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 12px",
          background: ACCENT2,
          borderRight: "1.5px solid #E0E7FF",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: ACCENT,
            whiteSpace: "nowrap",
          }}
        >
          Version
        </span>
      </div>
      <select
        value={value ?? ""}
        disabled={loading || versions.length === 0}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? null : isNaN(Number(v)) ? v : Number(v));
        }}
        style={{
          border: "none",
          background: "#fff",
          padding: "0 10px",
          fontSize: 13,
          fontWeight: 500,
          color: "#374151",
          outline: "none",
          cursor: "pointer",
          fontFamily: FONT_IP,
          minWidth: 100,
        }}
      >
        {loading && <option value="">Loading…</option>}
        {!loading && versions.length === 0 && <option value="">—</option>}
        {versions.map((v) => (
          <option key={v.id} value={v.id}>
            {v.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function MetricPicker({ value, onChange }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "stretch",
        border: "1.5px solid #E0E7FF",
        borderRadius: 8,
        overflow: "hidden",
        height: 34,
        fontFamily: FONT_IP,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 12px",
          background: ACCENT2,
          borderRight: "1.5px solid #E0E7FF",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: ACCENT,
            whiteSpace: "nowrap",
          }}
        >
          Metric
        </span>
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          border: "none",
          background: "#fff",
          padding: "0 10px",
          fontSize: 13,
          fontWeight: 500,
          color: "#374151",
          outline: "none",
          cursor: "pointer",
          fontFamily: FONT_IP,
          minWidth: 100,
        }}
      >
        {METRIC_OPTIONS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ─── Icons ─────────────────────────────────────────────────────── */
function ChevronIcon({ open }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      aria-hidden="true"
      style={{
        transition: "transform 0.18s ease",
        transform: open ? "rotate(90deg)" : "rotate(0deg)",
        flexShrink: 0,
      }}
    >
      <path
        d="M3 2l4 3-4 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
/* ─── IndirectTable (display-only, no data fetching) ────────────── */
function IndirectTable({
  title,
  subtitle,
  fyLabel,
  versionPicker,
  typeColumnLabel = "Type",
  groups,
  totalV,
  activeMonths,
  hiddenCols,
  actualThru = 0,
  isVariance = false,
}) {
  const classes = useStyles();

  const [expanded, setExpanded] = useState(() => {
    const s = new Set();
    groups.forEach((g) => {
      if (g.children.length > 0) s.add(g.id);
    });
    return s;
  });
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
    setPage(1);
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typeSort, setTypeSort] = useState(null);

  const [colFilters, setColFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});

  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);
  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setDropdownPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    repositionPanels();
  }, [repositionPanels]);

  /* Keep filter popovers anchored below header buttons during page/table scroll */
  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  /* also reset pagination when groups change (new filter applied from parent) */
  useEffect(() => {
    setPage(1);
  }, [groups]);

  /* A selected global month/quarter filter overrides the table-level column
     picker — e.g. hiding Jan via the picker then selecting Jan globally
     should bring the Jan column back, not keep it hidden. */
  const visibleMonths = IMONTHS.filter((m) =>
    activeMonths ? activeMonths.has(m) : !hiddenCols.has(m),
  );
  const showTotal = !hiddenCols.has("_total");

  const hasActiveFilter = (key) => {
    const f = colFilters[key];
    if (!f) return false;
    if (key === "_type") return !!(f.value && String(f.value).trim());
    return isNumFilterActive(f);
  };
  const setFilterValue = (key, value, type) => {
    setColFilters((prev) => ({
      ...prev,
      [key]: { type, op: (prev[key] && prev[key].op) || "gt", value },
    }));
    setPage(1);
  };
  const setFilterOp = (key, op) => {
    setColFilters((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { type: "num", value: "" }), op },
    }));
  };
  const clearFilter = (key) => {
    setColFilters((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
    setPage(1);
  };

  const toggleFilter = (key) => {
    if (openFilterKey === key) {
      setOpenFilterKey(null);
      return;
    }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  const toggleOpDropdown = (key) => {
    if (openOpKey === key) {
      setOpenOpKey(null);
      return;
    }
    const btn = opBtnRefs.current[key];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(key);
  };

  const filteredGroups = groups
    .map((grp) => {
      let result = grp;
      for (const [key, filter] of Object.entries(colFilters)) {
        if (!hasActiveFilter(key)) continue;
        if (key === "_type") {
          const q = filter.value.toLowerCase();
          if (result.type.toLowerCase().includes(q)) continue;
          const matchedChildren = result.children.filter((c) =>
            c.type.toLowerCase().includes(q),
          );
          if (!matchedChildren.length) return null;
          const v = matchedChildren.reduce((acc, c) => vAdd(acc, c.v), zeroV());
          result = { ...result, children: matchedChildren, v, total: vSum(v) };
        } else if (key === "_total") {
          if (!applyNumFilter(result.total, filter)) return null;
        } else {
          const idx = IMONTHS.indexOf(key);
          if (idx !== -1) {
            const matchedChildren = result.children.filter((c) =>
              applyNumFilter(c.v[idx], filter),
            );
            if (!matchedChildren.length) return null;
            const v = matchedChildren.reduce((acc, c) => vAdd(acc, c.v), zeroV());
            result = { ...result, children: matchedChildren, v, total: vSum(v) };
          }
        }
      }
      return result;
    })
    .filter(Boolean);

  const sortedGroups = typeSort === null
    ? filteredGroups
    : filteredGroups.map((grp) => ({
        ...grp,
        children: [...grp.children].sort((a, b) =>
          typeSort === "asc"
            ? a.type.localeCompare(b.type)
            : b.type.localeCompare(a.type),
        ),
      }));

  // Paginate expanded children; fall back to group-level if nothing is expanded
  const allExpandedChildren = [];
  sortedGroups.forEach((grp) => {
    if (expanded.has(grp.id)) {
      grp.children.forEach((_, ci) =>
        allExpandedChildren.push({ grpId: grp.id, ci }),
      );
    }
  });
  const paginateChildren = allExpandedChildren.length > 0;
  const totalItems = paginateChildren
    ? allExpandedChildren.length
    : sortedGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedChildKeys = paginateChildren
    ? new Set(
        allExpandedChildren
          .slice((safePage - 1) * pageSize, safePage * pageSize)
          .map(({ grpId, ci }) => `${grpId}:${ci}`),
      )
    : null;
  const visibleGroups = paginateChildren
    ? sortedGroups
    : sortedGroups.slice((safePage - 1) * pageSize, safePage * pageSize);

  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const ps = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...ps]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  const getFilterLabel = (key) => {
    if (key === "_type") return typeColumnLabel;
    if (key === "_total") return "Total";
    return key;
  };

  const renderFilterContent = (key) => {
    const isText = key === "_type";
    const filter = colFilters[key] || { type: isText ? "text" : "num", op: "gt", value: "" };
    if (isText) {
      return (
        <TextFilterContent
          label={getFilterLabel(key)}
          value={filter.value}
          onChange={(v) => setFilterValue(key, v, "text")}
          onClear={() => clearFilter(key)}
          placeholder="Filter…"
        />
      );
    }
    return (
      <NumericFilterContent
        label={getFilterLabel(key)}
        value={filter.value}
        op={filter.op || "gt"}
        onValueChange={(v) => setFilterValue(key, v, "num")}
        onClear={() => clearFilter(key)}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  const hasTableFilters = Object.entries(colFilters).some(([key]) => hasActiveFilter(key));
  const effectiveTotalV = hasTableFilters
    ? filteredGroups.reduce((acc, grp) => vAdd(acc, grp.v), zeroV())
    : totalV;

  const totalVVisible = visibleMonths.map((m) => effectiveTotalV[IMONTHS.indexOf(m)]);
  const calcTotal = (v) =>
    activeMonths
      ? IMONTHS.reduce((s, m, i) => (activeMonths.has(m) ? s + v[i] : s), 0)
      : vSum(v);
  const TABLE_W = TYPE_W + IMONTHS.length * COL_W + TOTAL_W;

  return (
    <div className={classes.tableSection}>
      <div className={classes.tableSectionHead}>
        <div>
          <h2 className={classes.sectionTitle}>{title}</h2>
          {subtitle && <div className={classes.sectionMeta}>{subtitle}</div>}
        </div>
        <div className={classes.headRight}>
          {versionPicker}
        </div>
      </div>

      <div className={classes.tableBody}>
      <div className={classes.tableWrap} onScroll={handleTableScroll}>
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
            fontFamily: FONT_IP,
            fontSize: 12,
            width: TABLE_W,
            minWidth: TABLE_W,
          }}
        >
          <colgroup>
            <col style={{ width: TYPE_W }} />
            {visibleMonths.map((m) => (
              <col key={m} style={{ width: COL_W }} />
            ))}
            {showTotal && <col style={{ width: TOTAL_W }} />}
          </colgroup>
          <thead style={{ background: TH_BG }}>
            <tr style={{ height: GH }}>
              <th style={thFixedBlank()} aria-hidden />
              <th
                colSpan={visibleMonths.length + (showTotal ? 1 : 0)}
                style={thBandGroup()}
              >
                {fyLabel}
              </th>
            </tr>
            <tr style={{ height: CH }}>
              <th style={thFixedCol()}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      color: openFilterKey === "_type" ? "#60A5FA" : undefined,
                    }}
                  >
                    {typeColumnLabel}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <button
                      className={`${classes.filterIconBtn} ${typeSort ? classes.filterIconBtnActive : ""}`}
                      onClick={() => setTypeSort((s) => s === null ? "asc" : s === "asc" ? "desc" : null)}
                      title={typeSort === "asc" ? "Sorted A→Z (click for Z→A)" : typeSort === "desc" ? "Sorted Z→A (click to clear)" : `Sort by ${typeColumnLabel}`}
                      style={{ fontSize: 11, width: 18, height: 16 }}
                    >
                      {typeSort === "asc" ? "↑" : typeSort === "desc" ? "↓" : "↕"}
                    </button>
                    <button
                      ref={(el) => {
                        filterBtnRefs.current["_type"] = el;
                      }}
                      data-filter-btn
                      className={`${classes.filterIconBtn} ${hasActiveFilter("_type") ? classes.filterIconBtnActive : ""}`}
                      onClick={() => toggleFilter("_type")}
                      title={`Filter ${typeColumnLabel}`}
                    >
                      <FilterFunnelIcon />
                    </button>
                  </div>
                </div>
              </th>
              {visibleMonths.map((m) => {
                const isActualM = IMONTHS.indexOf(m) < actualThru;
                return (
                <th key={m} style={thMonth(isActualM)}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        color: openFilterKey === m ? "#60A5FA" : undefined,
                      }}
                    >
                      {m}
                    </span>
                    <button
                      ref={(el) => {
                        filterBtnRefs.current[m] = el;
                      }}
                      data-filter-btn
                      className={`${classes.filterIconBtn} ${hasActiveFilter(m) ? classes.filterIconBtnActive : ""}`}
                      onClick={() => toggleFilter(m)}
                      title={`Filter ${m}`}
                      style={isActualM ? { color: TH_ACTUAL_TEXT, opacity: 1 } : undefined}
                    >
                      <FilterFunnelIcon />
                    </button>
                  </div>
                </th>
                );
              })}
              {showTotal && (
                <th style={isVariance ? { ...thMonth(false), fontWeight: 700 } : thTotal()}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        color:
                          openFilterKey === "_total" ? "#60A5FA" : undefined,
                      }}
                    >
                      Total
                    </span>
                    <button
                      ref={(el) => {
                        filterBtnRefs.current["_total"] = el;
                      }}
                      data-filter-btn
                      className={`${classes.filterIconBtn} ${hasActiveFilter("_total") ? classes.filterIconBtnActive : ""}`}
                      onClick={() => toggleFilter("_total")}
                      title="Filter Total"
                    >
                      <FilterFunnelIcon />
                    </button>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr style={{ height: RH }}>
              <td style={tdTotalFixed()}>Total</td>
              {totalVVisible.map((val, i) => (
                <td
                  key={i}
                  style={{
                    ...tdTotalScroll(),
                    color: isVariance ? varColor(val) : val < 0 ? "#DC2626" : TOTAL_FG,
                  }}
                >
                  {fmtI(val)}
                </td>
              ))}
              {showTotal && (
                <td
                  style={{
                    ...tdTotalCol(true),
                    ...(isVariance ? { color: varColor(calcTotal(effectiveTotalV)) } : {}),
                  }}
                >
                  {fmtI(calcTotal(effectiveTotalV))}
                </td>
              )}
            </tr>
            {visibleGroups.map((grp) => {
              const isOpen = expanded.has(grp.id);
              const hasChildren = grp.children.length > 0;
              return (
                <React.Fragment key={grp.id}>
                  <tr
                    style={{
                      height: RH,
                      cursor: hasChildren ? "pointer" : "default",
                    }}
                    onClick={() => hasChildren && toggleExpand(grp.id)}
                  >
                    <td style={tdGroupFixed()}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          color: "#1E3A5F",
                        }}
                      >
                        {hasChildren && <ChevronIcon open={isOpen} />}
                        <strong>{grp.type}</strong>
                      </span>
                    </td>
                    {visibleMonths.map((m) => {
                      const val = grp.v[IMONTHS.indexOf(m)];
                      return (
                        <td
                          key={m}
                          style={{
                            ...tdGroupScroll(),
                            color: isVariance ? varColor(val) : val < 0 ? "#DC2626" : "#1E3A5F",
                          }}
                        >
                          {fmtI(val)}
                        </td>
                      );
                    })}
                    {showTotal && (
                      <td
                        style={{
                          ...tdGroupScroll(),
                          color: isVariance
                            ? varColor(calcTotal(grp.v))
                            : calcTotal(grp.v) < 0
                              ? "#DC2626"
                              : "#1E3A5F",
                          fontWeight: 700,
                        }}
                      >
                        {fmtI(calcTotal(grp.v))}
                      </td>
                    )}
                  </tr>
                  {isOpen &&
                    grp.children.map((child, ci) => {
                      if (
                        pagedChildKeys &&
                        !pagedChildKeys.has(`${grp.id}:${ci}`)
                      )
                        return null;
                      const bg = ci % 2 === 0 ? EVEN_BG : ODD_BG;
                      return (
                        <tr key={ci} style={{ height: RH }}>
                          <td
                            style={{ ...tdFixed(bg), paddingLeft: 30 }}
                            title={child.type}
                          >
                            {child.type}
                          </td>
                          {visibleMonths.map((m) => {
                            const val = child.v[IMONTHS.indexOf(m)];
                            return (
                              <td
                                key={m}
                                style={{
                                  ...tdBase(bg),
                                  color: isVariance ? varColor(val) : val < 0 ? "#DC2626" : CELL_FG,
                                }}
                              >
                                {fmtI(val)}
                              </td>
                            );
                          })}
                          {showTotal && (
                            <td
                              style={{
                                ...tdBase("#F8FAFC"),
                                color: isVariance
                                  ? varColor(calcTotal(child.v))
                                  : calcTotal(child.v) < 0
                                    ? "#DC2626"
                                    : CELL_FG,
                                fontWeight: 600,
                              }}
                            >
                              {fmtI(calcTotal(child.v))}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={classes.paginationBar}>
        <span className={classes.paginationInfo}>
          {paginateChildren
            ? `${allExpandedChildren.length} row${allExpandedChildren.length !== 1 ? "s" : ""}`
            : sortedGroups.length === groups.length
              ? `${groups.length} group${groups.length !== 1 ? "s" : ""}`
              : `${sortedGroups.length} of ${groups.length} groups`}
        </span>
        <div className={classes.paginationRight}>
          <span className={classes.pageSizeLabel}>Rows Per Page</span>
          <select
            className={classes.pageSizeSelect}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100, ...(totalItems > 100 ? [totalItems] : [])].map(
              (n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ),
            )}
          </select>
          {totalPages > 1 && (
            <div className={classes.paginationControls}>
              <button
                className={classes.pageBtn}
                disabled={safePage === 1}
                onClick={() => setPage(1)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="7,2 3,6 7,10" />
                  <polyline points="11,2 7,6 11,10" />
                </svg>
              </button>
              <button
                className={classes.pageBtn}
                disabled={safePage === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="8,2 4,6 8,10" />
                </svg>
              </button>
              {pageNumbers.map((p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && pageNumbers[i - 1] < p - 1 && (
                    <span className={classes.pageDots}>…</span>
                  )}
                  <button
                    className={`${classes.pageBtn} ${p === safePage ? classes.pageBtnActive : ""}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
              <button
                className={classes.pageBtn}
                disabled={safePage === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="4,2 8,6 4,10" />
                </svg>
              </button>
              <button
                className={classes.pageBtn}
                disabled={safePage === totalPages}
                onClick={() => setPage(totalPages)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="5,2 9,6 5,10" />
                  <polyline points="1,2 5,6 1,10" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      </div>

      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            className={classes.filterPanel}
            style={{ top: filterPanelPos.top, left: filterPanelPos.left }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey]?.op) || "gt"}
            onSelect={(op) => { setFilterOp(openOpKey, op); setOpenOpKey(null); }}
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          />,
          document.body,
        )}
    </div>
  );
}

/* ─── Page component – fetches data, passes to IndirectTable ─────── */
export default function IndirectPL({ filters = {}, downloadRef, setParentDownloading, onHeaderMonthChange }) {
  const classes = useStyles();

  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedVersionBdg, setSelectedVersionBdg] = useState(null);
  const [metricType, setMetricType] = useState(METRIC_OPTIONS[0]);

  const [tableData, setTableData] = useState({
    af: { groups: [], totalV: zeroV(), totalSum: 0 },
    bdg: { groups: [], totalV: zeroV(), totalSum: 0 },
    variance: { groups: [], totalV: zeroV(), totalSum: 0 },
  });
  /* Each table's version can have a different actual-vs-forecast cutoff (or none at
     all, e.g. a pure Budget version) — computed independently per table from its own
     API response, not shared across AF/Budget/Variance. */
  const [afActualThru, setAfActualThru] = useState(0);
  const [bdgActualThru, setBdgActualThru] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hiddenCols, setHiddenCols] = useState(() => new Set());

  const toggleCol = useCallback((key) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const showAllCols = useCallback(() => setHiddenCols(new Set()), []);

  const handleDownload = async () => {
    setParentDownloading?.(true);
    try {
      const f = withPeriodFiltersForDownload(filters, {
        year: String(filters.year || "2026"),
        version: versions.find((v) => v.id === selectedVersionBdg)?.label ?? "",
      });
      if (metricType) f.metricSelection = metricType;
      if (filters.department?.length) f.department = filters.department;
      if (filters.subDepartment?.length) f.subDepartment = filters.subDepartment;
      if (filters.employeeName?.length) f.employeeName = filters.employeeName;
      if (filters.costTypeIndirect?.length) f.costTypeIndirect = filters.costTypeIndirect;
      const { blob, filename } = await downloadIndirectPLAPI(f);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (_) { /* silent */ } finally { setParentDownloading?.(false); }
  };
  if (downloadRef) downloadRef.current = handleDownload;

  /* Fetch version list whenever year changes; auto-select by label with fallback */
  useEffect(() => {
    setVersionsLoading(true);
    setSelectedVersion(null);
    setSelectedVersionBdg(null);
    getIndirectVersionsAPI()
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const opts = normalizeVersions(data);
        setVersions(opts);
        if (opts.length > 0) {
          setSelectedVersion(opts[0].id);
          setSelectedVersionBdg(
            (opts.find((v) => v.label === "Budget 2026") || opts[0]).id,
          );
        }
      })
      .catch(() => {})
      .finally(() => setVersionsLoading(false));
  }, [filters.year]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Fetch table data — AF uses selectedVersion, Budget uses selectedVersionBdg */
  useEffect(() => {
    if (selectedVersion === null || selectedVersionBdg === null) return;

    setLoading(true);
    setError(null);

    const afFilters = buildApiFilters(filters, selectedVersion, metricType);
    const bdgFilters = buildApiFilters(filters, selectedVersionBdg, metricType);

    Promise.all([
      getIndirectLaborMonthsAPI(afFilters),
      getIndirectLaborBudgetMonthsAPI(bdgFilters),
      getIndirectNonLaborMonthsAPI(afFilters),
      getIndirectNonLaborBudgetMonthsAPI(bdgFilters),
    ])
      .then(([laborAF, laborBdg, nonLaborAF, nonLaborBdg]) => {
        const afGroups = buildGroups(
          extractRows(laborAF),
          extractRows(nonLaborAF),
        );
        const bdgGroups = buildGroups(
          extractRows(laborBdg),
          extractRows(nonLaborBdg),
        );
        const varGroups = buildVarianceGroups(afGroups, bdgGroups);

        const afTotalV = computeGrandTotalV(afGroups);
        const bdgTotalV = computeGrandTotalV(bdgGroups);
        const varTotalV = vSub(afTotalV, bdgTotalV);

        const actualMonths = extractActualMonths(laborAF);
        onHeaderMonthChange?.(actualMonths.length > 0 ? actualMonths[actualMonths.length - 1] : null);
        setAfActualThru(actualMonths.length);
        setBdgActualThru(extractActualMonths(laborBdg).length);

        setTableData({
          af: { groups: afGroups, totalV: afTotalV, totalSum: vSum(afTotalV) },
          bdg: {
            groups: bdgGroups,
            totalV: bdgTotalV,
            totalSum: vSum(bdgTotalV),
          },
          variance: {
            groups: varGroups,
            totalV: varTotalV,
            totalSum: vSum(varTotalV),
          },
        });
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load Indirect P&L data. Please try again.");
        setLoading(false);
      });
  }, [JSON.stringify(filters), selectedVersion, selectedVersionBdg, metricType]); // eslint-disable-line react-hooks/exhaustive-deps

  if (versionsLoading || loading) {
    return <Loading />;
  }
  if (error) {
    return (
      <div className={`${classes.stateBox} ${classes.errorText}`}>
        {error}
        <br />
        <button className={classes.retryBtn} onClick={() => setError(null)}>
          Retry
        </button>
      </div>
    );
  }

  const { af, bdg, variance } = tableData;
  const fyLabel =
    filters.year && filters.year !== "All" ? `FY-${filters.year}` : "All Years";
  const bdgVersionLabel =
    versions.find((v) => v.id === selectedVersionBdg)?.label ?? fyLabel;
  const afVersionLabel =
    versions.find((v) => v.id === selectedVersion)?.label ?? fyLabel;

  /* Client-side cost type filter — API returns all types regardless */
  const selectedCostTypes =
    Array.isArray(filters.costTypeIndirect) &&
    filters.costTypeIndirect.length > 0
      ? filters.costTypeIndirect
      : null;
  const filterGroups = (groups) =>
    selectedCostTypes
      ? groups.filter((g) => selectedCostTypes.includes(g.type))
      : groups;

  const afGroups  = filterGroups(af.groups);
  const bdgGroups = filterGroups(bdg.groups);
  const varGroups = filterGroups(variance.groups);
  const afTotalV  = computeGrandTotalV(afGroups);
  const bdgTotalV = computeGrandTotalV(bdgGroups);
  const varTotalV = computeGrandTotalV(varGroups);

  const activeMonths = deriveActiveMonths(filters);

  return (
    <div>
      <div className={classes.colPickerToolbar}>
        <MetricPicker value={metricType} onChange={setMetricType} />
        <IndirectPlColumnPicker
          classes={classes}
          months={IMONTHS}
          hiddenCols={hiddenCols}
          activeMonths={activeMonths}
          onToggleCol={toggleCol}
          onShowAllCols={showAllCols}
        />
      </div>
      <IndirectTable
        title="Indirect Actual + Forecast"
        subtitle={`Revenue & Costs · ${afVersionLabel}`}
        fyLabel={fyLabel}
        typeColumnLabel={metricType}
        groups={afGroups}
        totalV={afTotalV}
        totalSum={vSum(afTotalV)}
        activeMonths={activeMonths}
        hiddenCols={hiddenCols}
        actualThru={afActualThru}
      />
      <IndirectTable
        title="Budget"
        subtitle={`Approved Budget · ${bdgVersionLabel}`}
        fyLabel={fyLabel}
        typeColumnLabel={metricType}
        versionPicker={
          <VersionPicker
            versions={versions}
            value={selectedVersionBdg}
            onChange={setSelectedVersionBdg}
            loading={versionsLoading}
          />
        }
        groups={bdgGroups}
        totalV={bdgTotalV}
        totalSum={vSum(bdgTotalV)}
        activeMonths={activeMonths}
        hiddenCols={hiddenCols}
        actualThru={bdgActualThru}
      />
      <IndirectTable
        title="Variance"
        subtitle="Actual + Forecast Vs Budget"
        fyLabel={fyLabel}
        typeColumnLabel={metricType}
        groups={varGroups}
        totalV={varTotalV}
        totalSum={vSum(varTotalV)}
        activeMonths={activeMonths}
        hiddenCols={hiddenCols}
        actualThru={afActualThru}
        isVariance
      />
    </div>
  );
}
